import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { 
  CartItem, 
  createShopifyCart, 
  addLineToShopifyCart,
  updateShopifyCartLine,
  removeLineFromShopifyCart,
  fetchCart,
  applyDiscountCode as shopifyApplyDiscountCode
} from '@/lib/shopify';

export interface SubscriptionInfo {
  enabled: boolean;
  plan: string;
  termMonths: number;
  frequency: string;
  monthIndex: number;
  pricePerPack: number;
  packType: "3-pack" | "12-pack" | "single" | "2-pack";
  subscriptionKey: string;
  baseVariantId: string;
}

export interface CartItemWithSubscription extends CartItem {
  lineId?: string | null;
  subscription?: SubscriptionInfo;
}

interface CartStore {
  items: CartItemWithSubscription[];
  cartId: string | null;
  checkoutUrl: string | null;
  isLoading: boolean;
  isSyncing: boolean;
  discountCode: string | null;
  discountApplied: boolean;
  loyaltyPointsApplied: number;
  loyaltyDiscountAmount: number;
  
  // Actions
  addItem: (item: CartItemWithSubscription) => Promise<void>;
  updateQuantity: (variantId: string, quantity: number) => Promise<void>;
  removeItem: (variantId: string) => Promise<void>;
  clearCart: () => void;
  setCartId: (cartId: string) => void;
  setCheckoutUrl: (url: string) => void;
  setLoading: (loading: boolean) => void;
  syncCart: () => Promise<void>;
  applyDiscountCode: (code: string) => Promise<{ success: boolean; error?: string }>;
  applyLoyaltyPoints: (points: number, discountAmount: number) => void;
  removeLoyaltyPoints: () => void;
  getCheckoutUrl: () => string | null;
  getTotalItems: () => number;
  getTotalPrice: () => number;
  getFinalPrice: () => number;
  getSubscriptionItems: () => CartItemWithSubscription[];
  getOneTimeItems: () => CartItemWithSubscription[];
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      cartId: null,
      checkoutUrl: null,
      isLoading: false,
      isSyncing: false,
      discountCode: null,
      discountApplied: false,
      loyaltyPointsApplied: 0,
      loyaltyDiscountAmount: 0,

      addItem: async (item) => {
        const { items, cartId, clearCart } = get();
        
        // Check for existing item
        const existingItem = items.find(i => {
          if (item.subscription?.enabled) {
            return i.variantId === item.variantId && i.subscription?.enabled;
          }
          return i.variantId === item.variantId && !i.subscription?.enabled;
        });

        set({ isLoading: true });
        
        try {
          if (!cartId) {
            // Create new cart with first item
            const result = await createShopifyCart(item);
            if (result) {
              set({
                cartId: result.cartId,
                checkoutUrl: result.checkoutUrl,
                items: [{ ...item, lineId: result.lineId }],
                isLoading: false
              });
            } else {
              // Fallback to local-only if API fails
              set({ 
                items: [{ ...item, lineId: null }],
                isLoading: false
              });
            }
          } else if (existingItem) {
            // Update quantity of existing item
            const newQuantity = existingItem.quantity + item.quantity;
            
            if (existingItem.lineId) {
              const result = await updateShopifyCartLine(cartId, existingItem.lineId, newQuantity);
              if (result.success) {
                const currentItems = get().items;
                set({ 
                  items: currentItems.map(i => {
                    const isMatch = item.subscription?.enabled
                      ? i.variantId === item.variantId && i.subscription?.enabled
                      : i.variantId === item.variantId && !i.subscription?.enabled;
                    return isMatch ? { ...i, quantity: newQuantity } : i;
                  }),
                  checkoutUrl: result.checkoutUrl || get().checkoutUrl,
                  isLoading: false
                });
              } else if (result.cartNotFound) {
                // Cart expired — update locally and clear cart ID so next operation recreates
                const currentItems = get().items;
                set({
                  cartId: null,
                  checkoutUrl: null,
                  items: currentItems.map(i => {
                    const isMatch = item.subscription?.enabled
                      ? i.variantId === item.variantId && i.subscription?.enabled
                      : i.variantId === item.variantId && !i.subscription?.enabled;
                    return isMatch ? { ...i, quantity: newQuantity, lineId: null } : { ...i, lineId: null };
                  }),
                  isLoading: false
                });
              }
            } else {
              // Update local only
              set({
                items: items.map(i => {
                  const isMatch = item.subscription?.enabled
                    ? i.variantId === item.variantId && i.subscription?.enabled
                    : i.variantId === item.variantId && !i.subscription?.enabled;
                  return isMatch ? { ...i, quantity: newQuantity } : i;
                }),
                isLoading: false
              });
            }
          } else {
            // Add new line to existing cart
            const result = await addLineToShopifyCart(cartId, item);
            if (result.success) {
              const currentItems = get().items;
              set({ 
                items: [...currentItems, { ...item, lineId: result.lineId ?? null }],
                checkoutUrl: result.checkoutUrl || get().checkoutUrl,
                isLoading: false
              });
            } else if (result.cartNotFound) {
              // Cart expired — recreate with ALL existing items + new item
              const existingItems = get().items;
              set({ cartId: null, checkoutUrl: null });
              // Create new cart with the new item first
              const createResult = await createShopifyCart(item);
              if (createResult) {
                const rebuiltItems: CartItemWithSubscription[] = [{ ...item, lineId: createResult.lineId }];
                set({ cartId: createResult.cartId, checkoutUrl: createResult.checkoutUrl });
                // Re-add all previously existing items
                for (const existing of existingItems) {
                  const addResult = await addLineToShopifyCart(createResult.cartId, existing);
                  rebuiltItems.push({ ...existing, lineId: addResult.lineId ?? null });
                }
                set({ items: rebuiltItems, isLoading: false });
              } else {
                set({ items: [...existingItems, { ...item, lineId: null }], isLoading: false });
              }
            } else {
              // Fallback to local
              set({
                items: [...items, { ...item, lineId: null }],
                isLoading: false
              });
            }
          }
        } catch (error) {
          console.error('Failed to add item:', error);
          // Fallback to local-only cart
          if (existingItem) {
            set({
              items: items.map(i => {
                const isMatch = item.subscription?.enabled
                  ? i.variantId === item.variantId && i.subscription?.enabled
                  : i.variantId === item.variantId && !i.subscription?.enabled;
                return isMatch ? { ...i, quantity: i.quantity + item.quantity } : i;
              }),
              isLoading: false
            });
          } else {
            set({
              items: [...items, { ...item, lineId: null }],
              isLoading: false
            });
          }
        }
      },

      updateQuantity: async (variantId, quantity) => {
        if (quantity <= 0) {
          await get().removeItem(variantId);
          return;
        }
        
        const { items, cartId, clearCart } = get();
        const item = items.find(i => i.variantId === variantId);
        if (!item) return;

        set({ isLoading: true });
        
        try {
          if (item.lineId && cartId) {
            const result = await updateShopifyCartLine(cartId, item.lineId, quantity);
            if (result.success) {
              const currentItems = get().items;
              set({ 
                items: currentItems.map(i => i.variantId === variantId ? { ...i, quantity } : i),
                checkoutUrl: result.checkoutUrl || get().checkoutUrl,
                isLoading: false
              });
            } else if (result.cartNotFound) {
              clearCart();
            } else {
              set({ isLoading: false });
            }
          } else {
            set({
              items: items.map(i => i.variantId === variantId ? { ...i, quantity } : i),
              isLoading: false
            });
          }
        } catch (error) {
          console.error('Failed to update quantity:', error);
          set({
            items: items.map(i => i.variantId === variantId ? { ...i, quantity } : i),
            isLoading: false
          });
        }
      },

      removeItem: async (variantId) => {
        const { items, cartId, clearCart } = get();
        const item = items.find(i => i.variantId === variantId);
        if (!item) return;

        set({ isLoading: true });
        
        try {
          if (item.lineId && cartId) {
            const result = await removeLineFromShopifyCart(cartId, item.lineId);
            if (result.success) {
              const currentItems = get().items;
              const newItems = currentItems.filter(i => i.variantId !== variantId);
              if (newItems.length === 0) {
                clearCart();
              } else {
                set({ 
                  items: newItems,
                  checkoutUrl: result.checkoutUrl || get().checkoutUrl,
                  isLoading: false
                });
              }
            } else if (result.cartNotFound) {
              clearCart();
            } else {
              set({ isLoading: false });
            }
          } else {
            const newItems = items.filter(i => i.variantId !== variantId);
            if (newItems.length === 0) {
              clearCart();
            } else {
              set({ items: newItems, isLoading: false });
            }
          }
        } catch (error) {
          console.error('Failed to remove item:', error);
          const newItems = items.filter(i => i.variantId !== variantId);
          set({ items: newItems, isLoading: false });
        }
      },

      clearCart: () => {
        set({ 
          items: [], 
          cartId: null, 
          checkoutUrl: null,
          discountCode: null,
          discountApplied: false,
          loyaltyPointsApplied: 0,
          loyaltyDiscountAmount: 0,
          isLoading: false
        });
      },

      setCartId: (cartId) => set({ cartId }),
      setCheckoutUrl: (checkoutUrl) => set({ checkoutUrl }),
      setLoading: (isLoading) => set({ isLoading }),

      syncCart: async () => {
        const { cartId, isSyncing, clearCart } = get();
        if (!cartId || isSyncing) return;

        set({ isSyncing: true });
        
        try {
          const result = await fetchCart(cartId);
          if (!result.exists || result.totalQuantity === 0) {
            clearCart();
          } else if (result.checkoutUrl) {
            // Update checkout URL to ensure it has the channel parameter
            set({ checkoutUrl: result.checkoutUrl });
          }
        } catch (error) {
          console.error('Failed to sync cart with Shopify:', error);
        } finally {
          set({ isSyncing: false });
        }
      },

      applyDiscountCode: async (code: string) => {
        const { cartId, items } = get();
        
        // If no cart exists but we have items, create a cart first
        if (!cartId && items.length > 0) {
          set({ isLoading: true });
          try {
            // Create a new cart with existing items
            const firstItem = items[0];
            const result = await createShopifyCart(firstItem);
            if (result) {
              set({
                cartId: result.cartId,
                checkoutUrl: result.checkoutUrl,
                items: items.map((item, index) => 
                  index === 0 ? { ...item, lineId: result.lineId } : item
                )
              });
              
              // Add remaining items to cart
              for (let i = 1; i < items.length; i++) {
                const addResult = await addLineToShopifyCart(result.cartId, items[i]);
                if (addResult.success && addResult.lineId) {
                  const currentItems = get().items;
                  set({
                    items: currentItems.map((item, idx) => 
                      idx === i ? { ...item, lineId: addResult.lineId } : item
                    )
                  });
                }
              }
            } else {
              set({ isLoading: false });
              return { success: false, error: 'Unable to create cart. Please try again.' };
            }
          } catch (error) {
            console.error('Failed to create cart for discount:', error);
            set({ isLoading: false });
            return { success: false, error: 'Failed to create cart. Please try again.' };
          }
        }
        
        const currentCartId = get().cartId;
        if (!currentCartId) {
          set({ isLoading: false });
          return { success: false, error: 'No items in cart' };
        }

        set({ isLoading: true });
        
        try {
          const result = await shopifyApplyDiscountCode(currentCartId, code);
          
          if (result.success && result.applicable) {
            set({ 
              discountCode: code,
              discountApplied: true,
              isLoading: false
            });
            return { success: true };
          } else if (result.success && !result.applicable) {
            set({ isLoading: false });
            return { success: false, error: 'This discount code is not applicable to your cart' };
          } else {
            set({ isLoading: false });
            return { success: false, error: result.error || 'Failed to apply discount code' };
          }
        } catch (error) {
          console.error('Failed to apply discount code:', error);
          set({ isLoading: false });
          return { success: false, error: 'Failed to apply discount code' };
        }
      },

      getCheckoutUrl: () => {
        const checkoutUrl = get().checkoutUrl;
        if (!checkoutUrl) return null;
        // Ensure checkout URL always has channel parameter
        try {
          const url = new URL(checkoutUrl);
          url.searchParams.set('channel', 'online_store');
          return url.toString();
        } catch {
          return checkoutUrl;
        }
      },

      getTotalItems: () => {
        return get().items.reduce((sum, item) => sum + item.quantity, 0);
      },

      getTotalPrice: () => {
        return get().items.reduce((sum, item) => sum + (parseFloat(item.price.amount) * item.quantity), 0);
      },

      getFinalPrice: () => {
        const total = get().items.reduce((sum, item) => sum + (parseFloat(item.price.amount) * item.quantity), 0);
        return Math.max(0, total - get().loyaltyDiscountAmount);
      },

      applyLoyaltyPoints: (points: number, discountAmount: number) => {
        set({ 
          loyaltyPointsApplied: points, 
          loyaltyDiscountAmount: discountAmount 
        });
      },

      removeLoyaltyPoints: () => {
        set({ 
          loyaltyPointsApplied: 0, 
          loyaltyDiscountAmount: 0 
        });
      },

      getSubscriptionItems: () => {
        return get().items.filter(item => item.subscription?.enabled);
      },

      getOneTimeItems: () => {
        return get().items.filter(item => !item.subscription?.enabled);
      },
    }),
    {
      name: 'grizzlee-gripz-cart',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ 
        items: state.items, 
        cartId: state.cartId, 
        checkoutUrl: state.checkoutUrl,
        discountCode: state.discountCode,
        discountApplied: state.discountApplied,
        loyaltyPointsApplied: state.loyaltyPointsApplied,
        loyaltyDiscountAmount: state.loyaltyDiscountAmount
      }),
    }
  )
);